import React from 'react';
import { useFormContext, useWatch } from 'react-hook-form';

import ColumnLabel from '@/main/components/common/columnLabel/columnLabel';
import KogCheckBoxControlled from '@/main/components/shared/checkbox/checkBoxControlled';
import KogAutoCompleteControlled from '@/main/components/shared/inputAutoComplete/autoCompleteControlled';
import KogInputTextControlledNew from '@/main/components/shared/inputText/inputTextControlledNew';
import KOGImage from '@/main/components/shared/kogImage/kogImage';
import WrapperQuoteProcurementPartInfo from '@/main/components/shared/quoteProcurementPartInfo/quoteProcurementPartInfo';
import { KeyValueDefaultEnum } from '@/main/models/common/keyValueDefaultEnum';
import { getTranslateByKey, isNullUndefined } from '@/main/services/utils';
import { convertStringForBoolean } from '@/main/services/utilsString';
import { useReasonReducer } from '@/main/store/reason';

import { IFormValues } from '../modalBulkCancelClaim';
import { IPartCancel, ISupplierCancel } from '../types';
import { getNameFormPart, getNameFormSupplier } from '../utilsmodalBulkCancelClaim';
import * as S from './styles';

interface ISupplierBulkCancelClaimProps {
  indexSupplier: number;
  supplier: ISupplierCancel;
}
function SupplierBulkCancelClaim(props: ISupplierBulkCancelClaimProps) {
  const { reason: reasonReducer } = useReasonReducer();
  const {
    control,
    getValues,
    clearErrors,
    setValue
  } = useFormContext<IFormValues>();
  const watcherReason = useWatch({
    control,
    name: getNameFormSupplier('reason', props.indexSupplier)
  }) as KeyValueDefaultEnum | null;

  function handleToggleCheckboxSupplier(checked: boolean) {
    props.supplier.updateableParts.forEach((part, indexPart) => {
      const nameFormPart = getNameFormPart('isSelected', props.indexSupplier, indexPart);
      setValue(nameFormPart, checked);
    });

    const parts = getValues(`suppliers.${props.indexSupplier}.updateableParts`);
    const isUnselectedAll = parts.every((part) => !part.isSelected);

    if (isUnselectedAll) {
      const nameReasonFormSupplier = getNameFormSupplier('reason', props.indexSupplier);
      const nameReasonDescFormSupplier = getNameFormSupplier('reasonDescription', props.indexSupplier);

      setValue(nameReasonFormSupplier, null);
      setValue(nameReasonDescFormSupplier, '');
      clearErrors([nameReasonFormSupplier, nameReasonDescFormSupplier]);
    }
  }

  const hasSomePartSelected = () => {
    const parts = getValues(getNameFormSupplier('updateableParts', props.indexSupplier)) as IPartCancel[];
    const hasSomePartSelected = parts.some((part) => part.isSelected);

    return hasSomePartSelected
  };

  const showReasonDescription = !isNullUndefined(watcherReason) && convertStringForBoolean(watcherReason.default)

  return (
    <S.ContainerProcurement>
      <S.ContainerSupplier>
        <S.SupplierName>
          {props.supplier.supplierName}
        </S.SupplierName>
        <KogCheckBoxControlled
          control={control}
          label=''
          disabled={false}
          nameCustom={getNameFormSupplier('isSelectedAll', props.indexSupplier)}
          onChangeCustom={handleToggleCheckboxSupplier}
        />
      </S.ContainerSupplier>
      {props.supplier.updateableParts.map((part, indexPart) => (
        <PartSupplierBulkCancelClaim
          key={`${props.indexSupplier}-${part.guidProcurementSupplierItem}`}
          part={part}
          indexPart={indexPart}
          {...props}
        />
      ))}

      <S.AreaReasonSupplier>
        <KogAutoCompleteControlled
          id={`id-${getNameFormSupplier('reason', props.indexSupplier)}`}
          nameCustom={getNameFormSupplier('reason', props.indexSupplier)}
          label={getTranslateByKey('information.reason')}
          isDefaultValueUnique
          required={hasSomePartSelected()}
          rulesCustom={{ required: hasSomePartSelected() }}
          disabled={!hasSomePartSelected()}
          control={control}
          setValueCustom={setValue}
          options={reasonReducer.data}
          isLoading={reasonReducer.isLoading}
          style={{ margin: '1rem 0' }}
          onChangeCustom={option => { }}
        />
        {showReasonDescription && (
          <KogInputTextControlledNew
            id='id-searchOrder-input-description'
            label={getTranslateByKey('information.description')}
            name={getNameFormSupplier('reasonDescription', props.indexSupplier)}
            control={control}
            required={showReasonDescription}
          />
        )}
      </S.AreaReasonSupplier>
    </S.ContainerProcurement>
  )
}

interface IPartSupplierBulkCancelClaimProps extends ISupplierBulkCancelClaimProps {
  part: IPartCancel;
  indexPart: number;

}
function PartSupplierBulkCancelClaim({
  part,
  indexSupplier,
  indexPart,
  supplier
}: IPartSupplierBulkCancelClaimProps) {
  const {
    control,
    getValues,
    clearErrors,
    setValue
  } = useFormContext<IFormValues>();

  function handleToggleCheckbox(checked: boolean) {
    const parts = getValues(`suppliers.${indexSupplier}.updateableParts`);
    const nameFormPart = getNameFormPart('isSelected', indexSupplier, indexPart);
    setValue(nameFormPart, checked);

    const isSelectedAll = parts.every((part) => part.isSelected);
    const nameFormSupplier = getNameFormSupplier('isSelectedAll', indexSupplier);
    setValue(nameFormSupplier, isSelectedAll);

    const isUnselectedAll = parts.every((part) => !part.isSelected);

    if (isUnselectedAll) {
      const nameReasonFormSupplier = getNameFormSupplier('reason', indexSupplier);
      const nameReasonDescFormSupplier = getNameFormSupplier('reasonDescription', indexSupplier);

      setValue(nameReasonFormSupplier, null);
      setValue(nameReasonDescFormSupplier, '');
      clearErrors([nameReasonFormSupplier, nameReasonDescFormSupplier]);
    }

  }

  return (
    <S.ContainerPart>
      <S.Column customWidth='16%'>
        <S.BoxImagePart>
          <KOGImage src={part.partImage} />
        </S.BoxImagePart>
        <WrapperQuoteProcurementPartInfo
          data={part.quoteProcurementPartInfo}
          renderType="column"
        />
        <ColumnLabel
          label={''}
          content={<S.Quantity> {part.partQuantity}</S.Quantity>}
        />
        <S.AreaCheckboxPart>
          <KogCheckBoxControlled
            control={control}
            label=''
            disabled={false}
            nameCustom={getNameFormPart('isSelected', indexSupplier, indexPart)}
            onChangeCustom={handleToggleCheckbox}
          />
        </S.AreaCheckboxPart>
      </S.Column>
    </S.ContainerPart>
  )
}

export default SupplierBulkCancelClaim